import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styled from 'styled-components';
import { styledWithProps } from '../utils';
import { Resolution, instanceOfResolution } from '../data/Resolution';
import { hoverMixin } from 'utils/style';

interface LabelWrapperProps {
  active: boolean;
  size: 'default' | 'small';
}
const LabelWrapper = styledWithProps<LabelWrapperProps>()(styled.div)`
  height: ${props => (props.size === 'small' ? 20 : 26)}px;
  font-size: ${props => (props.size === 'small' ? 12 : 15)}px;
  line-height: ${props => (props.size === 'small' ? 20 : 26)}px;
  text-transform: ${props => (props.size === 'small' ? 'uppercase' : 'none')};
  padding-left: 8px;
  padding-right: 25px;
  border-radius: ${props => (props.active ? '4px 4px 0 0;' : '4px')};
  cursor: pointer;
  * {
    cursor: pointer;
  }
  background-position: center right;
  background-repeat: no-repeat;
  background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjZweCIgaGVpZ2h0PSIyMHB4IiB2aWV3Qm94PSIwIDAgMjYgMjAiIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB4bWxuczp4bGluaz0iaHR0cDovL3d3dy53My5vcmcvMTk5OS94bGluayI+CiAgICA8ZyBpZD0iU3ltYm9scyIgc3Ryb2tlPSJub25lIiBzdHJva2Utd2lkdGg9IjEiIGZpbGw9Im5vbmUiIGZpbGwtcnVsZT0iZXZlbm9kZCI+CiAgICAgICAgPGcgaWQ9InNlbGVjdC1kb3duLWFycm93IiBmaWxsPSIjMWExYTFhIj4KICAgICAgICAgICAgPHBvbHlnb24gaWQ9IlJlY3RhbmdsZS00IiBwb2ludHM9IjE4LjA3MTA2NzggOCAxMy4wMzU1MzM5IDEzLjAzNTUzMzkgOCA4Ij48L3BvbHlnb24+CiAgICAgICAgPC9nPgogICAgPC9nPgo8L3N2Zz4=');
  background-color: ${props =>
    props.active ? 'rgba(0,0,0, 0.1)' : 'transparent'};
  ${props =>
    hoverMixin(
      `background-color: ${
        props.active ? 'rgba(0,0,0, 0.1)' : 'rgba(0,0,0, 0.05)'
      };`
    )}

  .templates-filters & {
    height: 40px;
    border-radius: 100px;
    padding-top: 5px;
    padding-bottom: 5px;
    border: solid 1px rgba(var(--primary-fg-rgb), 0.1);
    padding-left: 24px;
    padding-right: 40px;
    background-position: center right 16px;
  }
`;

interface LabelProps {
  active: boolean;
  legend?: string;
  value: string;
  size: 'default' | 'small';
  onClick: () => void;
}

const Label: React.FunctionComponent<LabelProps> = props => {
  return (
    <LabelWrapper
      active={props.active}
      size={props.size}
      onClick={props.onClick}
    >
      {props.legend && (
        <span style={{ color: 'var(--secondary-text-color)', marginRight: 5 }}>
          {props.legend}:
        </span>
      )}
      <span style={{ fontWeight: 'bold' }}>{props.value}</span>
    </LabelWrapper>
  );
};

export interface ItemValue {
  id: string;
  label: string;
}

export interface ItemProps {
  value: any | ItemValue;
  selected: boolean;
  onSelected: (value: any | ItemValue) => void;
}

const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const Panel = styled.div`
  position: absolute;
  z-index: 1;
  top: 100%;
  left: 0;
  background-color: var(--menu-bg-color);
  text-align: left;
  padding: 5px 0;
  border-radius: 0 4px 4px 4px;
  box-shadow: var(--menu-shadow);
  min-width: 100%;
`;

const AddOption = styled.div`
  height: 40px;
  padding-left: 30px;
  padding-right: 20px;
  white-space: nowrap;
  font-size: 15px;
  line-height: 40px;

  cursor: pointer;
  * {
    cursor: pointer;
  }

  ${hoverMixin('background-color: var(--menu-bg-color-hover);')}
`;

interface Props {
  legend?: string;
  options: any[];
  value: any | ItemValue;
  formatter?: (value: any) => string;
  itemRenderer: React.ComponentType<ItemProps>;
  onSelect: (value: any) => void;
  addLabel?: string;
  onAdd?: () => void;
  size?: 'default' | 'small';
}

interface State {
  open: boolean;
}

export default class extends React.Component<Props, State> {
  state = {
    open: false
  };
  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.open !== this.state.open) {
      if (this.state.open) {
        window.addEventListener('mousedown', this.didClickOnWindow);
      } else {
        window.removeEventListener('mousedown', this.didClickOnWindow);
      }
    }
  }
  componentWillUnmount() {
    window.removeEventListener('mousedown', this.didClickOnWindow);
  }
  didClickOnWindow = (e: MouseEvent) => {
    const el = ReactDOM.findDOMNode(this)!;
    if (!el.contains(e.target as HTMLElement)) {
      this.setState({ open: false });
    }
  };
  didSelectOption = (value: any) => {
    this.setState({ open: false }, () => {
      this.props.onSelect(value);
    });
  };
  toggleState = () => {
    this.setState({ open: !this.state.open });
  };
  render() {
    const { itemRenderer: InjectedComponent, value, formatter } = this.props;
    let legendLabel: string;
    if (formatter) {
      legendLabel = formatter(value);
    } else {
      legendLabel = (value.label ? value.label : value) as string;
    }
    return (
      <Wrapper>
        <Label
          active={this.state.open}
          legend={this.props.legend}
          value={legendLabel}
          size={this.props.size || 'default'}
          onClick={this.toggleState}
        />
        {this.state.open && (
          <Panel className="menu-panel">
            {this.props.options.map((option: any, index: number) => {
              let selected = this.props.value === option;
              if (instanceOfResolution(this.props.value)) {
                const selectedRes = this.props.value as Resolution;
                const optionRes = option as Resolution;
                selected =
                  selectedRes.width === optionRes.width &&
                  selectedRes.height === optionRes.height;
              }
              return (
                <InjectedComponent
                  value={option}
                  selected={selected}
                  onSelected={this.didSelectOption}
                  key={index}
                />
              );
            })}
            {this.props.addLabel && (
              <AddOption onClick={this.props.onAdd}>
                {this.props.addLabel}
              </AddOption>
            )}
          </Panel>
        )}
      </Wrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/SelectRich.tsx